import { useState } from "react";
import { X, PaperPlaneTilt } from "@phosphor-icons/react";
import client from "../lib/api";
import { toast } from "sonner";

/**
 * SendInvoiceModal — lightweight modal to preview + send a branded PDF invoice
 * to the customer via Resend. Pre-fills the customer_email on the invoice; pilots
 * can override before sending. On success flips the invoice draft→sent so the
 * table state matches reality without a page refresh.
 */
export default function SendInvoiceModal({ invoice, onClose, onSent }) {
  const [to, setTo] = useState(invoice.customer_email || "");
  const [message, setMessage] = useState("");
  const [attachLoadDocs, setAttachLoadDocs] = useState(true);
  const [sending, setSending] = useState(false);

  if (!invoice) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!to) {
      toast.error("Recipient email is required");
      return;
    }
    setSending(true);
    try {
      const r = await client.post(`/invoices/${invoice.id}/send-email`, {
        to_email: to,
        message: message || undefined,
        attach_load_docs: attachLoadDocs,
      });
      const attached = r.data?.load_docs_attached || 0;
      toast.success(
        attached > 0
          ? `Invoice + ${attached} load doc${attached !== 1 ? "s" : ""} emailed to ${to}`
          : `Invoice emailed to ${to}`
      );
      onSent && onSent();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Send failed";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const amount = Number(invoice.amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="send-invoice-modal-backdrop"
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="send-invoice-modal"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#FF5A1F]">
              Email invoice
            </div>
            <h2 className="mt-1 text-xl font-bold text-white">
              Send #{invoice.invoice_number || invoice.id?.slice(0, 8)}
            </h2>
            <div className="mt-1 text-sm text-white/60">
              {invoice.customer} · <span className="font-mono">${amount}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-white/50 hover:bg-white/5 hover:text-white"
            data-testid="send-invoice-close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-white/60">
              Recipient email
            </label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="ap@customer.com"
              required
              className="mt-1.5 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#FF5A1F] focus:outline-none"
              data-testid="send-invoice-email-input"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-white/60">
              Message (optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a short note above the invoice details…"
              rows={3}
              className="mt-1.5 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#FF5A1F] focus:outline-none"
              data-testid="send-invoice-message-input"
            />
          </div>
          {invoice.load_reference && (
            <label
              className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 cursor-pointer hover:bg-white/[0.05]"
              data-testid="send-invoice-attach-docs-toggle"
            >
              <input
                type="checkbox"
                checked={attachLoadDocs}
                onChange={(e) => setAttachLoadDocs(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#FF5A1F]"
              />
              <div className="text-xs text-white/80">
                <div className="font-medium">Auto-attach load paperwork</div>
                <div className="text-white/50">
                  Include any Rate Con / BOL / POD you've uploaded for load <b>{invoice.load_reference}</b>.
                </div>
              </div>
            </label>
          )}
          <div className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-xs text-white/50">
            The branded PDF will be attached automatically. Replies route to your
            account email.
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="rounded-lg border border-white/10 bg-transparent px-4 py-2 text-sm font-medium text-white/70 hover:bg-white/5 disabled:opacity-50"
              data-testid="send-invoice-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending}
              className="inline-flex items-center gap-2 rounded-lg bg-[#FF5A1F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#FF5A1F]/90 disabled:opacity-50"
              data-testid="send-invoice-submit"
            >
              <PaperPlaneTilt size={16} weight="fill" />
              {sending ? "Sending…" : "Send invoice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
